import React, { useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { lineTypeMap } from '../../utils/commonUtils';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import {
  FolderOpen as FolderOpenIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import './OrgTreeSelect.css';

// ✅ 개별 사용자 행을 메모이제이션
const UserRow = React.memo(({ user, isChecked, isProtected, onAction }) => {
  const handlePointerDown = (e) => {
    if (isProtected) return;
    e.preventDefault();
    onAction(user, true, !isChecked);
  };

  return (
    <tr
      className={isChecked ? 'selected' : ''}
      style={{ cursor: isProtected ? 'default' : 'pointer', userSelect: 'none' }}
    >
      <td className="text-center" onPointerDown={handlePointerDown}>
        <input type="checkbox" checked={isChecked} readOnly disabled={isProtected} style={{ pointerEvents: 'none' }} />
      </td>
      <td onPointerDown={handlePointerDown}>{user.name}</td>
      <td onPointerDown={handlePointerDown}>{user.position}</td>
      <td onPointerDown={handlePointerDown}>{user.userid}</td>
    </tr>
  );
});

/**
 * AprvLineSelect 컴포넌트 (OrgTreeSelect UI 적용)
 */
export default function AprvLineSelect({
  writer,
  onConfirm,
  onCancel,
  docStatus,
  initialSelected = []
}) {
  const [deptTree, setDeptTree] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allDeptCodes, setAllDeptCodes] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);

  const [deptSearchInput, setDeptSearchInput] = useState('');
  const [deptSearchTerm, setDeptSearchTerm] = useState('');

  const [userSearchInput, setUserSearchInput] = useState('');
  const [activeUserSearch, setActiveUserSearch] = useState('');

  const [selectedLines, setSelectedLines] = useState(initialSelected);
  const [currentLineType, setCurrentLineType] = useState('lb');
  const [activeDept, setActiveDept] = useState(null);

  useEffect(() => {
    axios.get('/api/orgchart')
      .then(res => {
        const { tree, allDeptCodes: codes, flatUsers } = buildDeptTree(res.data);
        setDeptTree(tree);
        setAllUsers(flatUsers);
        setAllDeptCodes(codes);
        setExpandedItems(codes);

        if (tree.length > 0) {
          setActiveDept(tree[0]);
        }
      })
      .catch(err => {
        console.error('조직도 불러오기 실패:', err);
      });
  }, []);

  const buildDeptTree = (depts) => {
    const map = {};
    const tree = [];
    const allDeptCodes = [];
    const flatUsers = [];

    depts.forEach(dept => {
      allDeptCodes.push(String(dept.dept_code));
      const usersWithDept = (dept.users || []).map(u => ({
        ...u,
        dept_code: dept.dept_code,
        dept_name: dept.dept_name,
        full_dept_name: dept.full_dept_name
      }));
      flatUsers.push(...usersWithDept);
      map[dept.dept_code] = { ...dept, children: [], users: usersWithDept };
    });

    depts.forEach(dept => {
      if (dept.parent_dept_code && map[dept.parent_dept_code]) {
        map[dept.parent_dept_code].children.push(map[dept.dept_code]);
      } else {
        tree.push(map[dept.dept_code]);
      }
    });

    return { tree, allDeptCodes, map, flatUsers };
  };

  const handleCheck = (item, isUser, checked) => {
    // ✅ 작성자 선택 방지
    if (writer && isUser && item.userid === writer.user_id) {
      alert('작성자는 결재선에 추가할 수 없습니다.');
      return;
    }

    // ✅ 보호 대상(수정/삭제 불가)은 체크박스로 해제 불가
    if (!checked && isTargetProtected(item, isUser)) {
      return;
    }

    // 사용자 선택
    if (isUser) {
      const lineObj = {
        user_id: item.userid,
        user_name: item.name,
        user_position: item.position,
        user_dept_code: item.dept_code,
        user_dept_name: item.dept_name,
        line_type: currentLineType,
        line_no: selectedLines.length + 1,
        current_yn: 'n',
        line_status: currentLineType === 'lr' ? '열람' : '대기',
        rep_yn: 'n',
        end_user_yn: 'n',
      };

      if (checked) {
        setSelectedLines(prev => [...prev, lineObj]);
      } else {
        setSelectedLines(prev => prev.filter(
          t => !(String(t.user_id) === String(lineObj.user_id) && !isDeptTargetLine(t))
        ));
      }
    }
    // 부서 선택 (수신/열람일 경우만 허용)
    else if (currentLineType === 'ls' || currentLineType === 'lr') {
      const lineObj = {
        user_id: item.dept_code,
        user_name: item.dept_name,
        user_position: null,
        user_dept_code: item.dept_code,
        user_dept_name: item.dept_name,
        line_type: currentLineType,
        line_no: selectedLines.length + 1,
        current_yn: 'n',
        line_status: '열람',
        rep_yn: 'n',
        end_user_yn: 'n',
      };

      if (checked) {
        setSelectedLines(prev => [...prev, lineObj]);
      } else {
        setSelectedLines(prev => prev.filter(
          t => !(String(t.user_dept_code) === String(lineObj.user_dept_code) && isDeptTargetLine(t))
        ));
      }
    }
  };

  const runDeptSearch = () => {
    setDeptSearchTerm(deptSearchInput);
  };

  const runUserSearch = () => {
    setActiveUserSearch(userSearchInput);
  };

  const clearUserSearch = () => {
    setUserSearchInput('');
    setActiveUserSearch('');
  };

  const displayUsers = useMemo(() => {
    if (!activeUserSearch) return activeDept?.users || [];
    const lowerSearch = activeUserSearch.toLowerCase();
    return allUsers.filter(u =>
      u.name.toLowerCase().includes(lowerSearch) ||
      String(u.userid).toLowerCase().includes(lowerSearch) ||
      (u.position && u.position.toLowerCase().includes(lowerSearch))
    );
  }, [activeUserSearch, activeDept, allUsers]);

  function isDeptTargetLine(line) {
    return (
      line?.target_type === 'dept' ||
      (String(line?.user_id) === String(line?.user_dept_code) && !line?.user_position)
    );
  }

  function isLineActionProtected(line) {
    const isProtected = line.done_date || line.current_yn === 'y';
    const isDraft = docStatus === 'draft' || line.doc_status === 'draft';
    return isProtected && !isDraft;
  }

  function isTargetProtected(item, isUser) {
    return selectedLines.some((line) => {
      const isMatched = isUser
        ? String(line.user_id) === String(item.userid) && !isDeptTargetLine(line)
        : String(line.user_dept_code) === String(item.dept_code) && isDeptTargetLine(line);
      return isMatched && isLineActionProtected(line);
    });
  }

  const renderTreeNodes = (nodes) => {
    return nodes.map(node => {
      const isDeptSelectable = currentLineType === 'ls' || currentLineType === 'lr';
      const isChecked = selectedLines.some(t => 
        String(t.user_dept_code) === String(node.dept_code) && isDeptTargetLine(t)
      );
      const isProtected = isTargetProtected(node, false);

      if (deptSearchTerm && !node.dept_name.includes(deptSearchTerm)) {
        const hasMatchingChild = JSON.stringify(node.children).includes(deptSearchTerm);
        if (!hasMatchingChild) return null;
      }

      const labelContent = (
        <div
          style={{ display: 'flex', alignItems: 'center', padding: '4px 0', width: '100%' }}
          onClick={(e) => {
            e.stopPropagation();
            setActiveDept(node);
            clearUserSearch();
            if (isDeptSelectable && !isProtected) {
              handleCheck(node, false, !isChecked);
            }
          }}
        >
          {isDeptSelectable && (
            <input
              type="checkbox"
              checked={isChecked}
              disabled={isProtected}
              onChange={(e) => handleCheck(node, false, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: '8px' }}
            />
          )}
          <FolderOpenIcon fontSize="small" sx={{ mr: 1, color: '#f6c23e' }} />
          <span style={{ fontWeight: activeDept?.dept_code === node.dept_code ? 'bold' : 'normal', color: '#333' }}>
            {node.dept_name}
          </span>
        </div>
      );

      return (
        <TreeItem key={String(node.dept_code)} itemId={String(node.dept_code)} label={labelContent}>
          {node.children?.length > 0 ? renderTreeNodes(node.children) : null}
        </TreeItem>
      );
    });
  };

  const moveLine = (index, direction) => {
    const approvers = selectedLines.filter(t => t.line_type === 'lb' || t.line_type === 'lc');
    const readers = selectedLines.filter(t => t.line_type === 'lr');

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= approvers.length) return;

    const current = approvers[index];
    const target = approvers[targetIndex];

    const isCurrentProtected = current.done_date || current.current_yn === 'y';
    const isTargetProtected = target.done_date || target.current_yn === 'y';

    if (isCurrentProtected || isTargetProtected) return;

    [approvers[index], approvers[targetIndex]] = [approvers[targetIndex], approvers[index]];

    const reordered = [
      ...approvers.map((t, idx) => ({ ...t, line_no: idx + 1 })),
      ...readers.map((t, idx) => ({ ...t, line_no: approvers.length + idx + 1 })),
    ];

    setSelectedLines(reordered);
  };

  const handleConfirm = () => {
    const approvers = selectedLines.filter(t => t.line_type === 'lb' || t.line_type === 'lc');

    if (approvers.length > 0) {
      const lastApprover = approvers[approvers.length - 1];
      if (lastApprover.line_type === 'lc') {
        alert('마지막 결재선은 합의가 될 수 없습니다. 결재로 지정해주세요.');
        return;
      }
    }

    const readers = selectedLines.filter(t => t.line_type === 'lr');
    const reordered = [
      ...approvers.map((t, idx) => ({ ...t, line_no: idx + 1 })),
      ...readers.map((t, idx) => ({ ...t, line_no: approvers.length + idx + 1 })),
    ];

    onConfirm(reordered);
  };

  return ReactDOM.createPortal(
    <div className="org-tree-popup" onPointerDown={e => e.stopPropagation()}>
      <div className="org-tree-popup-content">
        <div className="org-tree-header">
          <h5>📝 결재선 선택</h5>
          <IconButton onClick={onCancel} size="small"><CloseIcon /></IconButton>
        </div>

        <div className="org-tree-body">
          {/* 1. 트리 패널 (부서) */}
          <div className="org-tree-pane">
            <div className="org-tree-pane-header d-flex justify-content-between align-items-center">
              <span>조직 트리</span>
              <div className="d-flex gap-1">
                <button 
                  title="전체 펼치기"
                  className="btn btn-primary btn-xs p-0 d-flex align-items-center justify-content-center border-0 shadow-sm" 
                  style={{ width: '22px', height: '22px', fontSize: '14px', fontWeight: '900', borderRadius: '4px' }}
                  onClick={() => setExpandedItems(allDeptCodes)}
                >+</button>
                <button 
                  title="전체 접기"
                  className="btn btn-primary btn-xs p-0 d-flex align-items-center justify-content-center border-0 shadow-sm" 
                  style={{ width: '22px', height: '22px', fontSize: '14px', fontWeight: '900', borderRadius: '4px' }}
                  onClick={() => setExpandedItems([])}
                >-</button>
              </div>
            </div>
            <div className="p-2 border-bottom d-flex gap-1">
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="부서명 검색..."
                  value={deptSearchInput}
                  onChange={(e) => setDeptSearchInput(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') runDeptSearch(); }}
                  style={{ paddingRight: deptSearchInput ? '30px' : '8px' }}
                />
                {deptSearchInput && (
                  <IconButton
                    size="small"
                    onClick={() => { setDeptSearchInput(''); setDeptSearchTerm(''); }}
                    style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', padding: '2px' }}
                  >
                    <CloseIcon style={{ fontSize: '14px' }} />
                  </IconButton>
                )}
              </div>
              <button
                className="btn btn-outline-secondary btn-xs"
                style={{ padding: '0 8px', display: 'flex', alignItems: 'center' }}
                onClick={runDeptSearch}
              >
                <SearchIcon style={{ fontSize: '16px' }} />
              </button>
            </div>
            <div className="org-tree-pane-content premium-tree-root">
              <SimpleTreeView expandedItems={expandedItems} onExpandedItemsChange={(e, items) => setExpandedItems(items)}>
                {renderTreeNodes(deptTree)}
              </SimpleTreeView>
            </div>
          </div>

          {/* 2. 중앙 패널 (사용자 검색 및 목록) */}
          <div className="org-tree-pane">
            <div className="org-tree-pane-header">
              <span>👤 {activeUserSearch ? '전사 사용자 검색 결과' : (activeDept ? activeDept.dept_name : '부서원')}</span>
              <span className="badge bg-secondary">{displayUsers.length}명</span>
            </div>
            <div className="p-2 border-bottom d-flex gap-1">
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="찾으실 사원 성명 입력..."
                  value={userSearchInput}
                  onChange={(e) => setUserSearchInput(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') runUserSearch(); }}
                  style={{ paddingRight: userSearchInput ? '30px' : '8px' }}
                />
                {userSearchInput && (
                  <IconButton
                    size="small"
                    onClick={clearUserSearch}
                    style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', padding: '2px' }}
                  >
                    <CloseIcon style={{ fontSize: '14px' }} />
                  </IconButton>
                )}
              </div>
              <button
                className="btn btn-outline-secondary btn-xs"
                style={{ padding: '0 10px', display: 'flex', alignItems: 'center' }}
                onClick={runUserSearch}
              >
                <SearchIcon style={{ fontSize: '16px' }} />
              </button>
            </div>
            <div className="p-2 border-bottom">
              <label className="form-label mb-1" style={{ fontSize: '12px' }}>결재선 유형:</label>
              <select
                value={currentLineType}
                onChange={(e) => setCurrentLineType(e.target.value)}
                className="form-select form-select-sm"
              >
                <option value="lb">결재</option>
                <option value="lc">합의</option>
                <option value="lr">열람</option>
              </select>
            </div>
            <div className="org-tree-pane-content p-0">
              <table className="premium-user-table">
                <thead>
                  <tr>
                    <th width="40" className="text-center">선택</th>
                    <th>이름</th>
                    <th>직책</th>
                    <th>사번</th>
                  </tr>
                </thead>
                <tbody>
                  {displayUsers.length > 0 ? (
                    displayUsers.map(user => (
                      <UserRow
                        key={user.userid}
                        user={user}
                        isChecked={selectedLines.some(t => 
                          t.user_id === String(user.userid) && !isDeptTargetLine(t)
                        )}
                        isProtected={isTargetProtected(user, true)}
                        onAction={handleCheck}
                      />
                    ))
                  ) : (
                    <tr><td colSpan="4" className="text-center text-muted py-4">조회된 사원이 없습니다.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* 3. 우측 패널 */}
          <div className="org-tree-pane">
            <div className="org-tree-pane-header bg-primary text-white d-flex justify-content-between align-items-center">
              <div>
                <span>✅ 선택된 결재선</span>
                <span className="badge bg-light text-primary ms-2">{selectedLines.length}</span>
              </div>
            </div>
            <div className="org-tree-pane-content" style={{ backgroundColor: '#f8f9fa', overflowY: 'auto' }}>
              {/* 작성자 */}
              {writer && (
                <div className="mb-2 p-2 bg-light rounded" style={{ fontSize: '12px' }}>
                  <strong>🔑 기안자:</strong> {writer.user_name} ({writer.user_id})
                </div>
              )}

              {/* 결재/합의 그룹 */}
              {selectedLines.filter(t => t.line_type === 'lb' || t.line_type === 'lc').length > 0 && (
                <div className="mb-3">
                  <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>📝 결재 / 합의:</div>
                  {selectedLines
                    .filter(t => t.line_type === 'lb' || t.line_type === 'lc')
                    .map((t, idx) => {
                      const isProtected = t.done_date || t.current_yn === 'y';
                      const isDraft = docStatus === 'draft' || t.doc_status === 'draft';
                      const isActionProtected = isProtected && !isDraft;
                      const showProtectedBadge = isProtected && !isDraft;
                      return (
                        <div key={`${t.user_id}-${idx}`} className="selected-item shadow-sm mb-2">
                          <div className="selected-info">
                            <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{idx + 1}차</div>
                            <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                              <div style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {t.user_name} {t.user_position ? `(${t.user_position})` : ''}
                              </div>
                              <div style={{ fontSize: '10px', color: '#6c757d' }}>
                                {t.user_dept_name}
                              </div>
                            </div>
                            <span className="badge bg-primary" style={{ fontSize: '10px' }}>{lineTypeMap[t.line_type] || t.line_type}</span>
                          </div>
                          <div className="d-flex gap-1">
                            <IconButton
                              size="small"
                              onClick={() => moveLine(idx, 'up')}
                              disabled={isActionProtected || idx === 0}
                              style={{ padding: '2px' }}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveLine(idx, 'down')}
                              disabled={isActionProtected || idx === selectedLines.filter(t => t.line_type === 'lb' || t.line_type === 'lc').length - 1}
                              style={{ padding: '2px' }}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setSelectedLines(prev => prev.filter((_, i) => !(selectedLines.filter(x => x.line_type === 'lb' || x.line_type === 'lc')[idx] === _)))}
                              disabled={isActionProtected}
                              style={{ padding: '2px' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </div>
                          {showProtectedBadge && <span className="badge bg-danger" style={{ fontSize: '9px', marginLeft: '4px' }}>수정/삭제 불가</span>}
                        </div>
                      );
                    })}
              </div>
              )}

              {/* 열람 그룹 */}
              {selectedLines.filter(t => t.line_type === 'lr').length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>👀 열람:</div>
                  {selectedLines
                    .filter(t => t.line_type === 'lr')
                    .map((t, idx) => (
                      <div key={`${t.user_id}-${idx}`} className="selected-item shadow-sm mb-2">
                        <div className="selected-info">
                          <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{idx + 1}차</div>
                          <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                            <div style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {t.user_name} {t.user_position ? `(${t.user_position})` : ''}
                            </div>
                            <div style={{ fontSize: '10px', color: '#6c757d' }}>
                              {t.user_dept_name}
                            </div>
                          </div>
                          <span className="badge bg-info" style={{ fontSize: '10px' }}>{lineTypeMap[t.line_type] || t.line_type}</span>
                        </div>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setSelectedLines(prev => prev.filter((_, i) => !(selectedLines.filter(x => x.line_type === 'lr')[idx] === _)))}
                          style={{ padding: '2px' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                </div>
              )}

              {selectedLines.length === 0 && (
                <div className="text-center text-muted mt-5"><p>선택된 결재선이 없습니다.</p></div>
              )}
            </div>
          </div>
        </div>

        <div className="org-tree-footer">
          <button className="btn btn-success px-4" onClick={handleConfirm}>확인</button>
          <button className="btn btn-secondary px-4" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
